import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Stack} from '@mui/system';
import {translations} from '../../../../../../../../i18n/translations';
import FlatInput from '../../../../../../../../components/flat-input/flat-input';
import {useModalBtns} from '../../../../../../../../components/modal/use-modal-btns';
import {CompactModal} from '../../../../../../../../components/modal/compact-modal';
import {PatchAttachment} from '../../../../../../../../backend/requests/patch-attachment';
import {Attachment} from '../../../../../../../../backend/interfaces/attachment';

const description_max_length = 200;
const name_max_length = 100;

export type SeniorAttachmentsEditModalProps = {
  open: boolean;
  attachment: Attachment;
  canWrite?: boolean;
  onClose: () => void;
  onSave: (data: PatchAttachment) => void;
};

export function SeniorAttachmentsEditModal(props: SeniorAttachmentsEditModalProps) {
  const {t} = useTranslation();

  const {register, handleSubmit, formState, trigger, watch} = useForm<{
    name: string;
    description: string;
  }>({
    mode: 'onChange',
    defaultValues: {name: props.attachment.name, description: props.attachment.description},
  });

  // Trigger validation on mount to ensure initial values are valid
  useEffect(() => {
    trigger();
  }, [trigger]);

  const actionButtons = useModalBtns({
    left: {
      type: 'cancel',
      onClick: props.onClose,
    },
    right: props.canWrite
      ? {
          type: 'save',
          onClick: () => {
            handleSubmit((data) => {
              const validData: PatchAttachment = {
                name: data.name === '' ? null : data.name,
                description: data.description === '' ? null : data.description,
              };

              return props.onSave(validData);
            })();
          },
          disabled: formState.isSubmitting,
          loading: formState.isSubmitting,
        }
      : undefined,
  });

  return (
    <CompactModal
      open={props.open}
      onClose={props.onClose}
      title={
        t(translations.user_attachments.modals.edit.title) + ' "' + props.attachment.name + '"'
      }
      actions={actionButtons}
    >
      <Stack gap={2}>
        <FlatInput
          type="text"
          label={t(translations.user_attachments.modals.edit.name)}
          errors={formState.errors.name?.message}
          counter={{current: watch('name')?.length, max: name_max_length}}
          {...register('name', {
            required: t(translations.user_attachments.modals.edit.errors.name_required),
            validate: (value) => {
              if (value?.trim()?.length >= name_max_length) {
                return t(translations.user_attachments.modals.edit.errors.name_max_length, {
                  max: name_max_length,
                });
              }

              return true;
            },
          })}
          required
          disabled={!props.canWrite}
          disabledTooltip={t(translations.user_attachments.access_denied.edit)}
        />

        <FlatInput
          type="text"
          label={t(translations.user_attachments.modals.edit.description)}
          errors={formState.errors.description?.message}
          counter={{current: watch('description')?.length, max: description_max_length}}
          {...register('description', {
            validate: (value) => {
              if (value?.trim()?.length >= description_max_length) {
                return t(translations.user_attachments.modals.edit.errors.description_max_length, {
                  max: description_max_length,
                });
              }

              return true;
            },
          })}
          multiline
          rows={3}
          disabled={!props.canWrite}
          disabledTooltip={t(translations.user_attachments.access_denied.edit)}
        />
      </Stack>
    </CompactModal>
  );
}
